import React, { useState, useEffect, useRef, useCallback } from 'react';
import StrictDateInput from './StrictDateInput';
import { productAPI, settingsAPI, mastersAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

export const parseProductCustomFields = (product) => {
  const raw = product?.custom_fields;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const normalizeProduct = (product) => {
  if (!product) return null;
  return {
    ...product,
    custom_fields: parseProductCustomFields(product),
  };
};

export const buildProductForm = (product, initialValues = null) => {
  const normalized = normalizeProduct(product);
  const customFields = normalized?.custom_fields || {};
  const hasInitName = Boolean(initialValues?.name && initialValues.name.trim());
  return {
    name: normalized?.name || (initialValues?.name ?? ''),
    sku: normalized?.sku || (initialValues?.sku ?? ''),
    category: normalized?.category || (initialValues?.category ?? ''),
    subcategory: normalized?.subcategory || '',
    purchase_price: normalized?.purchase_price ?? (initialValues?.purchase_price ?? ''),
    selling_price: normalized?.selling_price ?? (initialValues?.selling_price ?? ''),
    gst_percent: normalized?.gst_percent ?? 0,
    current_stock: normalized?.current_stock ?? (initialValues?.current_stock ?? 0),
    low_stock_threshold: normalized?.low_stock_threshold ?? 10,
    hsn_sac: normalized?.hsn_sac || '',
    unit: normalized?.unit || (initialValues?.unit ?? 'Pcs'),
    item_type: normalized?.item_type || 'Product',
    brand: normalized?.brand || '',
    item_code: normalized?.item_code || '',
    print_name: normalized?.print_name || (initialValues?.name ?? ''),
    opening_stock_value: normalized?.opening_stock_value ?? '',
    min_sale_price: normalized?.min_sale_price ?? '',
    mrp: normalized?.mrp ?? '',
    sale_discount_percent: normalized?.sale_discount_percent ?? 0,
    reorder_level: normalized?.reorder_level ?? 0,
    description: normalized?.description || '',
    print_description: normalized?.print_description ?? false,
    one_click_sale: normalized?.one_click_sale ?? false,
    enable_tracking: normalized?.enable_tracking ?? false,
    print_serial: normalized?.print_serial ?? false,
    not_for_sale: normalized?.not_for_sale ?? false,
    product_type: normalized?.product_type || 'General',
    cess_rate: normalized?.cess_rate ?? 0,
    print_expiry_date: normalized?.print_expiry_date ?? false,
    sale_price_2: normalized?.sale_price_2 ?? '',
    sale_price_3: normalized?.sale_price_3 ?? '',
    barcode: normalized?.barcode || '',
    batch_number: normalized?.batch_number || '',
    serial_number: normalized?.serial_number || '',
    expiry_date: normalized?.expiry_date || '',
    manufacturing_date: normalized?.manufacturing_date || '',
    max_stock: normalized?.max_stock ?? '',
    opening_stock: normalized?.opening_stock ?? '',
    warehouse_location: normalized?.warehouse_location || '',
    color: normalized?.color || '',
    size: normalized?.size || '',
    weight: normalized?.weight ?? '',
    dimensions: normalized?.dimensions || '',
    model_number: normalized?.model_number || '',
    image_url: normalized?.image_url || '',
    status: normalized?.status || 'active',
    tags: normalized?.tags || '',
    wholesale_price: normalized?.wholesale_price ?? '',
    distributor_price: normalized?.distributor_price ?? '',
    dealer_price: normalized?.dealer_price ?? '',
    tax_inclusive: normalized?.tax_inclusive ?? false,
    reverse_charge: normalized?.reverse_charge ?? false,
    service_duration: normalized?.service_duration || '',
    service_location: normalized?.service_location || '',
    technician: normalized?.technician || '',
    appointment_required: normalized?.appointment_required ?? false,
    commission_percent: normalized?.commission_percent ?? '',
    cost_of_service: normalized?.cost_of_service ?? '',
    purchase_unit: normalized?.purchase_unit || '',
    selling_unit: normalized?.selling_unit || '',
    conversion_ratio: normalized?.conversion_ratio ?? '',
    cgst_rate: normalized?.cgst_rate ?? '',
    sgst_rate: normalized?.sgst_rate ?? '',
    igst_rate: normalized?.igst_rate ?? '',
    print_imei: normalized?.print_imei ?? false,
    internal_product: normalized?.internal_product ?? false,
    serial_tracking: normalized?.serial_tracking ?? false,
    warranty: normalized?.warranty ?? '',
    warranty_type: normalized?.warranty_type || 'months',
    second_uom_price_type: normalized?.second_uom_price_type || '',
    second_uom_price_value: normalized?.second_uom_price_value ?? '',
    second_unit_mrp: normalized?.second_unit_mrp ?? '',
    second_unit_min_sale_price: normalized?.second_unit_min_sale_price ?? '',
    location_rack: normalized?.location_rack || '',
    auto_fill_product_details: normalized ? !!customFields.auto_fill_product_details : !hasInitName,
    energy_star_rating: customFields.energy_star_rating || normalized?.energy_star_rating || '',
    item_description: customFields.item_description || normalized?.item_description || '',
  };
};

export const ProductCheckBox = ({ label, checked, onChange, darkMode, acc }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>
    <input type="checkbox" checked={checked || false} onChange={onChange} style={{ accentColor: acc }} /> {label}
  </label>
);

export const ProductModal = ({ product, initialData, onClose, onSuccess }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const normalizedProduct = normalizeProduct(product);
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState(() => buildProductForm(normalizedProduct, initialData));
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState('');
  const [error, setError] = useState('');
  const [fieldConfig, setFieldConfig] = useState({});
  const autoSaveTimer = useRef(null);
  const isDirty = useRef(false);
  const printNameEdited = useRef(!!normalizedProduct?.print_name);
  const set = k => e => { isDirty.current = true; setForm(f => ({ ...f, [k]: e.target.value })); };
  const setBool = k => e => { isDirty.current = true; setForm(f => ({ ...f, [k]: e.target.checked })); };

  const buildAutoDetails = useCallback((values) => {
    const parts = [
      values.brand,
      values.item_code,
      values.size,
      values.energy_star_rating,
      values.color,
      values.item_description,
      values.category,
    ]
      .map(v => (v || '').trim())
      .filter(Boolean);
    const name = parts.join(' ');
    const sku = parts
      .join('-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toUpperCase();
    return { name, sku };
  }, []);

  // Master data for dropdowns
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterSubcategories, setMasterSubcategories] = useState([]);

  useEffect(() => {
    mastersAPI.listBrands().then(r => setMasterBrands(r.data || [])).catch(() => {});
    mastersAPI.listCategories().then(r => setMasterCategories(r.data || [])).catch(() => {});
    mastersAPI.listSubcategories().then(r => setMasterSubcategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setForm(buildProductForm(normalizedProduct, initialData));
    isDirty.current = false;
    printNameEdited.current = !!normalizedProduct?.print_name;
    skuEdited.current = !!normalizedProduct?.sku && !normalizedProduct?.custom_fields?.auto_fill_product_details;
  }, [normalizedProduct, initialData]);

  // Auto-populate Print Name from Product Name (only when not manually edited)
  const handleNameChange = (e) => {
    isDirty.current = true;
    const val = e.target.value;
    setForm(f => ({
      ...f,
      name: val,
      print_name: printNameEdited.current ? f.print_name : val,
    }));
  };

  // Auto-generate SKU from brand + item_code
  const autoGenerateSku = useCallback((brand, itemCode) => {
    const b = (brand || '').trim().replace(/\s+/g, '-').toUpperCase();
    const c = (itemCode || '').trim().toUpperCase();
    if (!b && !c) return '';
    if (!b) return c;
    if (!c) return b;
    return `${b}-${c}`;
  }, []);

  // When brand or item_code changes, auto-generate SKU if not manually edited
  const skuEdited = useRef(!!normalizedProduct?.sku);
  const handleBrandChange = (val) => {
    isDirty.current = true;
    setForm(f => {
      const next = { ...f, brand: val };
      if (next.auto_fill_product_details) {
        const generated = buildAutoDetails(next);
        return { ...next, name: generated.name, sku: generated.sku };
      }
      const newSku = skuEdited.current ? f.sku : autoGenerateSku(val, f.item_code);
      return { ...next, sku: newSku };
    });
  };
  const handleItemCodeChange = (e) => {
    isDirty.current = true;
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, item_code: val };
      if (next.auto_fill_product_details) {
        const generated = buildAutoDetails(next);
        return { ...next, name: generated.name, sku: generated.sku };
      }
      const newSku = skuEdited.current ? f.sku : autoGenerateSku(f.brand, val);
      return { ...next, sku: newSku };
    });
  };

  useEffect(() => {
    setForm(f => {
      if (!f.auto_fill_product_details) return f;
      const generated = buildAutoDetails(f);
      if (f.name === generated.name && f.sku === generated.sku) return f;
      return { ...f, name: generated.name, sku: generated.sku };
    });
  }, [
    form.auto_fill_product_details,
    form.brand,
    form.item_code,
    form.size,
    form.energy_star_rating,
    form.color,
    form.item_description,
    form.category,
    buildAutoDetails,
  ]);

  // Filtered subcategories based on selected category
  const filteredSubcategories = masterSubcategories.filter(
    s => !form.category || s.category_name.toLowerCase() === form.category.toLowerCase()
  );

  // Save new brand to master
  const handleBrandSavePrompt = async (brandName) => {
    if (!brandName.trim()) return;
    const exists = masterBrands.some(b => b.brand_name.toLowerCase() === brandName.trim().toLowerCase());
    if (!exists) {
      const doSave = window.confirm(`"${brandName}" is not in the brand list. Save it to the brand master?`);
      if (doSave) {
        try {
          await mastersAPI.createBrand({ brand_name: brandName.trim() });
          const r = await mastersAPI.listBrands();
          setMasterBrands(r.data || []);
        } catch { /* already exists or error, ignore */ }
      }
    }
  };

  // Save new category to master
  const handleCategorySavePrompt = async (catName) => {
    if (!catName.trim()) return;
    const exists = masterCategories.some(c => c.category_name.toLowerCase() === catName.trim().toLowerCase());
    if (!exists) {
      const doSave = window.confirm(`"${catName}" is not in the category list. Save it to the category master?`);
      if (doSave) {
        try {
          await mastersAPI.createCategory({ category_name: catName.trim() });
          const r = await mastersAPI.listCategories();
          setMasterCategories(r.data || []);
        } catch { /* ignore */ }
      }
    }
  };

  const handleClose = useCallback(() => {
    if (isDirty.current && !window.confirm('Discard this product? All unsaved changes will be lost.')) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  // Load business settings for field config and defaults
  useEffect(() => {
    settingsAPI.get().then(r => {
      const d = r.data || {};
      let pfc = {};
      try {
        pfc = (typeof d.product_field_config === 'string' ? JSON.parse(d.product_field_config) : d.product_field_config) || {};
      } catch { pfc = {}; }
      setFieldConfig(pfc);
      // Apply defaults for new products
      if (!product) {
        setForm(f => ({
          ...f,
          gst_percent: d.default_gst_percent ?? f.gst_percent,
          unit: d.default_unit ?? f.unit,
          category: d.default_category ?? f.category,
        }));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showField = (key) => {
    const cfg = fieldConfig[key];
    if (!cfg) return true; // not configured = show by default
    return cfg.enabled !== false;
  };
  const isRequired = (key) => {
    const cfg = fieldConfig[key];
    return cfg?.mandatory === true;
  };

  const buildData = () => ({
    ...form,
    purchase_price: parseFloat(form.purchase_price) || 0,
    selling_price: parseFloat(form.selling_price) || 0,
    gst_percent: parseFloat(form.gst_percent) || 0,
    current_stock: parseInt(form.current_stock) || 0,
    low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
    min_sale_price: form.min_sale_price ? parseFloat(form.min_sale_price) : null,
    mrp: form.mrp ? parseFloat(form.mrp) : null,
    sale_discount_percent: parseFloat(form.sale_discount_percent) || 0,
    reorder_level: parseInt(form.reorder_level) || 0,
    cess_rate: parseFloat(form.cess_rate) || 0,
    opening_stock_value: parseFloat(form.opening_stock_value) || 0,
    sale_price_2: form.sale_price_2 ? parseFloat(form.sale_price_2) : null,
    sale_price_3: form.sale_price_3 ? parseFloat(form.sale_price_3) : null,
    wholesale_price: form.wholesale_price ? parseFloat(form.wholesale_price) : null,
    distributor_price: form.distributor_price ? parseFloat(form.distributor_price) : null,
    dealer_price: form.dealer_price ? parseFloat(form.dealer_price) : null,
    max_stock: form.max_stock ? parseInt(form.max_stock) : null,
    opening_stock: form.opening_stock ? parseInt(form.opening_stock) : null,
    weight: form.weight ? parseFloat(form.weight) : null,
    commission_percent: form.commission_percent ? parseFloat(form.commission_percent) : null,
    cost_of_service: form.cost_of_service ? parseFloat(form.cost_of_service) : null,
    conversion_ratio: form.conversion_ratio ? parseFloat(form.conversion_ratio) : null,
    expiry_date: form.expiry_date || null,
    manufacturing_date: form.manufacturing_date || null,
    cgst_rate: parseFloat(form.cgst_rate) || 0,
    sgst_rate: parseFloat(form.sgst_rate) || 0,
    igst_rate: parseFloat(form.igst_rate) || 0,
    warranty: form.warranty ? parseInt(form.warranty) : null,
    warranty_type: form.warranty_type || null,
    second_uom_price_value: form.second_uom_price_value ? parseFloat(form.second_uom_price_value) : null,
    second_unit_mrp: form.second_unit_mrp ? parseFloat(form.second_unit_mrp) : null,
    second_unit_min_sale_price: form.second_unit_min_sale_price ? parseFloat(form.second_unit_min_sale_price) : null,
    custom_fields: {
      ...(normalizedProduct?.custom_fields || {}),
      energy_star_rating: form.energy_star_rating || null,
      item_description: form.item_description || null,
      auto_fill_product_details: !!form.auto_fill_product_details,
    },
  });

  // Auto-save when editing existing product
  useEffect(() => {
    if (!product) return;
    if (!isDirty.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await productAPI.update(product.id, buildData());
        setAutoSaved('✓ Auto-saved');
        setTimeout(() => setAutoSaved(''), 3000);
      } catch (err) { console.error('Auto-save failed:', err); setAutoSaved('✗ Auto-save failed'); }
    }, 2500);
    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleSubmit = async e => {
    e.preventDefault(); setError('');
    if (!form.category.trim()) { setError('Category / Group is required'); return; }
    if (!form.subcategory.trim()) { setError('Subcategory is required'); return; }
    if (!form.brand.trim()) { setError('Brand is required'); return; }
    if (!form.item_code.trim()) { setError('Item Code(Model No.) is required'); return; }
    if (!form.name.trim()) { setError('Product Name is required'); return; }
    if (showField('sku') && !form.sku.trim()) { setError('SKU is required'); return; }
    if (!form.selling_price || parseFloat(form.selling_price) <= 0) { setError('Selling Price must be greater than 0'); return; }
    setSaving(true);
    try {
      let savedProduct = null;
      if (product) {
        const res = await productAPI.update(product.id, buildData());
        savedProduct = res?.data || product;
      } else {
        const res = await productAPI.create(buildData());
        savedProduct = res?.data || { ...buildData(), id: res?.data?.id };
      }
      onSuccess(savedProduct);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const renderCheckBox = (label, field) => (
    <ProductCheckBox label={label} checked={form[field]} onChange={setBool(field)} darkMode={darkMode} acc={acc} />
  );

  const tabs = [
    { id: 'basic', label: '📋 Basic' },
    { id: 'pricing', label: '💰 Pricing' },
    { id: 'tax', label: '📑 Tax' },
    { id: 'stock', label: '📦 Stock' },
    { id: 'physical', label: '📐 Physical' },
    ...(form.item_type === 'Service' ? [{ id: 'service', label: '🔧 Service' }] : []),
    { id: 'advanced', label: '⚙️ More' },
  ];

  const tabStyle = id => ({
    padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none',
    borderBottom: activeTab === id ? `2px solid ${acc}` : '2px solid transparent',
    background: 'transparent', color: activeTab === id ? acc : (darkMode ? '#94A3B8' : '#64748B'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 18, fontWeight: 800, margin: 0 }}>{product ? '✏️ Edit Product' : '➕ Add Product'}
              {autoSaved && <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 600, color: '#10B981' }}>{autoSaved}</span>}
            </h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${border}`, padding: '0 16px', overflowX: 'auto' }}>
            {tabs.map(t => <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} style={tabStyle(t.id)}>{t.label}</button>)}
          </div>
          {error && <div style={{ margin: '12px 24px 0', padding: '10px 14px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 13, flexShrink: 0 }}>{error}</div>}
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>

            {/* ── BASIC TAB ── */}
            {activeTab === 'basic' && <>
              <div><label style={lbl}>Item Type</label>
                <select style={inp} value={form.item_type} onChange={set('item_type')}>
                  <option value="Product">Product</option><option value="Service">Service</option>
                </select>
              </div>
              <div><label style={lbl}>Status</label>
                <select style={inp} value={form.status} onChange={set('status')}>
                  <option value="active">Active</option><option value="inactive">Inactive</option><option value="discontinued">Discontinued</option>
                </select>
              </div>
              {showField('category') && <div>
                <label style={lbl}>Category / Group <span style={req}>*</span></label>
                <input
                  list="category-list"
                  style={{ ...inp, border: !form.category.trim() ? '1px solid #ef4444' : inp.border }}
                  value={form.category}
                  onChange={e => { isDirty.current = true; setForm(f => ({ ...f, category: e.target.value, subcategory: '' })); }}
                  onBlur={e => handleCategorySavePrompt(e.target.value)}
                  placeholder="Select or type category"
                  required
                />
                <datalist id="category-list">
                  {masterCategories.map(c => <option key={c.id} value={c.category_name} />)}
                </datalist>
              </div>}
              <div>
                <label style={lbl}>Subcategory <span style={req}>*</span></label>
                <input
                  list="subcategory-list"
                  style={{ ...inp, border: !form.subcategory.trim() ? '1px solid #ef4444' : inp.border }}
                  value={form.subcategory}
                  onChange={set('subcategory')}
                  placeholder="Select or type subcategory"
                  required
                />
                <datalist id="subcategory-list">
                  {filteredSubcategories.map(s => <option key={s.id} value={s.subcategory_name} />)}
                </datalist>
              </div>
              {showField('brand') && <div>
                <label style={lbl}>Brand <span style={req}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input
                    list="brand-list"
                    style={{ ...inp, border: !form.brand.trim() ? '1px solid #ef4444' : inp.border }}
                    value={form.brand}
                    onChange={e => handleBrandChange(e.target.value)}
                    onBlur={e => handleBrandSavePrompt(e.target.value)}
                    placeholder="Select or type brand"
                    required
                  />
                  <datalist id="brand-list">
                    {masterBrands.map(b => <option key={b.id} value={b.brand_name} />)}
                  </datalist>
                </div>
              </div>}
              <div>
                <label style={lbl}>Item Code(Model No.)<span style={req}>*</span> <span style={{ ...muted, fontSize: 10, fontWeight: 400, textTransform: 'none' }}>(used to build SKU)</span></label>
                <input style={{ ...inp, border: !form.item_code.trim() ? '1px solid #ef4444' : inp.border }} value={form.item_code} onChange={handleItemCodeChange} placeholder="Supplier's code" required />
              </div>
              <div>
                <label style={lbl}>Size(Capacity)</label>
                <input style={inp} value={form.size} onChange={set('size')} placeholder="e.g. 1.5 Ton, 300 L, 64 GB" />
              </div>
              <div>
                <label style={lbl}>Energy(Star-Rating)</label>
                <input style={inp} value={form.energy_star_rating} onChange={set('energy_star_rating')} placeholder="e.g. 3 Star, 5 Star" />
              </div>
              <div>
                <label style={lbl}>Color(Design)</label>
                <input style={inp} value={form.color} onChange={set('color')} placeholder="e.g. Black, Silver, Floral" />
              </div>
              <div>
                <label style={lbl}>Item Description</label>
                <input style={inp} value={form.item_description} onChange={set('item_description')} placeholder="e.g. Inverter Split AC" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span>Auto Fill Product Details</span>
                  <span style={{ ...muted, fontSize: 10, fontWeight: 600, textTransform: 'none' }}>Default: ON</span>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: darkMode ? '#CBD5E1' : '#334155', fontSize: 13, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!form.auto_fill_product_details}
                    onChange={e => {
                      isDirty.current = true;
                      const enabled = e.target.checked;
                      setForm(f => {
                        const next = { ...f, auto_fill_product_details: enabled };
                        if (!enabled) return next;
                        const generated = buildAutoDetails(next);
                        return { ...next, name: generated.name, sku: generated.sku };
                      });
                      if (enabled) skuEdited.current = false;
                    }}
                    style={{ accentColor: acc }}
                  />
                  <span>{form.auto_fill_product_details ? 'ON - Name and SKU are auto-generated' : 'OFF - Name and SKU are manually editable'}</span>
                </label>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Product / Service Name<span style={req}>*</span></label>
                <input
                  style={{ ...inp, border: !form.name.trim() ? '1px solid #ef4444' : inp.border, background: form.auto_fill_product_details ? (darkMode ? '#0B1220' : '#EEF2F7') : inp.background }}
                  required
                  value={form.name}
                  onChange={handleNameChange}
                  placeholder="Product name"
                  disabled={form.auto_fill_product_details}
                />
              </div>
              <div>
                <label style={lbl}>Print Name <span style={{ ...muted, fontSize: 10, fontWeight: 400, textTransform: 'none' }}>(auto-fills from name)</span></label>
                <input style={inp} value={form.print_name}
                  onChange={e => { printNameEdited.current = true; set('print_name')(e); }}
                  placeholder="Name on invoice" />
              </div>
              {showField('sku') && <div>
                <label style={lbl}>SKU<span style={req}>*</span> <span style={{ ...muted, fontSize: 10, fontWeight: 400, textTransform: 'none' }}>(auto from brand+code)</span></label>
                <input
                  style={{ ...inp, border: !form.sku.trim() ? '1px solid #ef4444' : inp.border, background: form.auto_fill_product_details ? (darkMode ? '#0B1220' : '#EEF2F7') : inp.background }}
                  required
                  value={form.sku}
                  onChange={e => { skuEdited.current = true; set('sku')(e); }}
                  placeholder="BRAND-001"
                  disabled={form.auto_fill_product_details}
                />
              </div>}
              {showField('barcode') && <div><label style={lbl}>Barcode</label><input style={inp} value={form.barcode} onChange={set('barcode')} placeholder="Barcode number" /></div>}
              <div><label style={lbl}>Unit</label><input style={inp} value={form.unit} onChange={set('unit')} placeholder="Pcs, Kg, Ltr" /></div>
              <div><label style={lbl}>Tags</label><input style={inp} value={form.tags} onChange={set('tags')} placeholder="Comma-separated tags" /></div>
              <div><label style={lbl}>Image URL</label><input style={inp} value={form.image_url} onChange={set('image_url')} placeholder="https://..." /></div>
              {showField('description') && <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Description{isRequired('description') && <span style={req}>*</span>}</label><textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={form.description} onChange={set('description')} placeholder="Product description…" required={isRequired('description')} /></div>}
            </>}

            {/* ── PRICING TAB ── */}
            {activeTab === 'pricing' && <>
              {showField('purchase_price') && <div><label style={lbl}>Purchase Price (₹){isRequired('purchase_price') && <span style={req}>*</span>}</label><input style={inp} type="number" step="0.01" min="0" value={form.purchase_price} onChange={set('purchase_price')} required={isRequired('purchase_price')} /></div>}
              <div><label style={lbl}>Selling Price (₹)<span style={req}>*</span></label><input style={{ ...inp, border: (!form.selling_price || parseFloat(form.selling_price) <= 0) ? '1px solid #ef4444' : inp.border }} required type="number" step="0.01" min="0.01" value={form.selling_price} onChange={set('selling_price')} /></div>
              <div><label style={lbl}>Min. Sale Price (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.min_sale_price} onChange={set('min_sale_price')} placeholder="Minimum allowed" /></div>
              {showField('mrp') && <div><label style={lbl}>M.R.P. (₹){isRequired('mrp') && <span style={req}>*</span>}</label><input style={inp} type="number" step="0.01" min="0" value={form.mrp} onChange={set('mrp')} placeholder="Max retail price" required={isRequired('mrp')} /></div>}
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Multi-Price Tiers</label></div>
              {showField('wholesale_price') && <div><label style={lbl}>Wholesale Price (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.wholesale_price} onChange={set('wholesale_price')} /></div>}
              <div><label style={lbl}>Distributor Price (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.distributor_price} onChange={set('distributor_price')} /></div>
              <div><label style={lbl}>Dealer Price (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.dealer_price} onChange={set('dealer_price')} /></div>
              <div><label style={lbl}>Sale Price 2 (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.sale_price_2} onChange={set('sale_price_2')} /></div>
              <div><label style={lbl}>Sale Price 3 (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.sale_price_3} onChange={set('sale_price_3')} /></div>
              {showField('discount_percent') && <div><label style={lbl}>Sale Discount %</label><input style={inp} type="number" step="0.01" min="0" max="100" value={form.sale_discount_percent} onChange={set('sale_discount_percent')} /></div>}
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Unit Conversion</label></div>
              <div><label style={lbl}>Purchase Unit</label><input style={inp} value={form.purchase_unit} onChange={set('purchase_unit')} placeholder="Box, Carton" /></div>
              <div><label style={lbl}>Selling Unit</label><input style={inp} value={form.selling_unit} onChange={set('selling_unit')} placeholder="Pcs, Strip" /></div>
              <div><label style={lbl}>Conversion Ratio</label><input style={inp} type="number" step="0.01" min="0" value={form.conversion_ratio} onChange={set('conversion_ratio')} placeholder="e.g. 12 (1 box = 12 pcs)" /></div>
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Second Unit Pricing</label></div>
              <div><label style={lbl}>Second UOM Price Type</label>
                <select style={inp} value={form.second_uom_price_type} onChange={set('second_uom_price_type')}>
                  <option value="">-- None --</option><option value="fixed">Fixed</option><option value="percent">Percent</option>
                </select>
              </div>
              <div><label style={lbl}>Second UOM Price Value</label><input style={inp} type="number" step="0.01" min="0" value={form.second_uom_price_value} onChange={set('second_uom_price_value')} /></div>
              <div><label style={lbl}>Second Unit MRP (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.second_unit_mrp} onChange={set('second_unit_mrp')} /></div>
              <div><label style={lbl}>Second Unit Min Sale Price (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.second_unit_min_sale_price} onChange={set('second_unit_min_sale_price')} /></div>
            </>}

            {/* ── TAX TAB ── */}
            {activeTab === 'tax' && <>
              {showField('gst_percent') && <div><label style={lbl}>GST %{isRequired('gst_percent') && <span style={req}>*</span>}</label><input style={inp} type="number" step="0.01" min="0" max="100" value={form.gst_percent} onChange={set('gst_percent')} required={isRequired('gst_percent')} /></div>}
              {showField('hsn_sac') && <div><label style={lbl}>HSN / SAC Code{isRequired('hsn_sac') && <span style={req}>*</span>}</label><input style={inp} value={form.hsn_sac} onChange={set('hsn_sac')} placeholder="8 digit HSN code" required={isRequired('hsn_sac')} /></div>}
              {showField('cess_percent') && <div><label style={lbl}>Cess Rate %</label><input style={inp} type="number" step="0.01" min="0" value={form.cess_rate} onChange={set('cess_rate')} /></div>}
              <div><label style={lbl}>Product Type</label>
                <select style={inp} value={form.product_type} onChange={set('product_type')}>
                  <option value="General">General</option><option value="Apparel">Apparel</option><option value="Footwear">Footwear</option>
                  <option value="Medical">Medical</option><option value="Food">Food</option><option value="Electronics">Electronics</option>
                </select>
              </div>
              <div>{renderCheckBox("Tax Inclusive (Price includes GST)", "tax_inclusive")}</div>
              <div>{renderCheckBox("Reverse Charge Applicable", "reverse_charge")}</div>
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Split GST Rates (Optional)</label></div>
              <div><label style={lbl}>CGST Rate %</label><input style={inp} type="number" step="0.01" min="0" value={form.cgst_rate} onChange={set('cgst_rate')} placeholder="Auto from GST%" /></div>
              <div><label style={lbl}>SGST Rate %</label><input style={inp} type="number" step="0.01" min="0" value={form.sgst_rate} onChange={set('sgst_rate')} placeholder="Auto from GST%" /></div>
              <div><label style={lbl}>IGST Rate %</label><input style={inp} type="number" step="0.01" min="0" value={form.igst_rate} onChange={set('igst_rate')} placeholder="For interstate" /></div>
            </>}

            {/* ── STOCK TAB ── */}
            {activeTab === 'stock' && <>
              {showField('current_stock') && <div><label style={lbl}>Current Stock</label><input style={inp} type="number" min="0" value={form.current_stock} onChange={set('current_stock')} /></div>}
              <div><label style={lbl}>Opening Stock Qty</label><input style={inp} type="number" min="0" value={form.opening_stock} onChange={set('opening_stock')} /></div>
              <div><label style={lbl}>Opening Stock Value (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.opening_stock_value} onChange={set('opening_stock_value')} /></div>
              {showField('low_stock_threshold') && <div><label style={lbl}>Low Stock Alert</label><input style={inp} type="number" min="0" value={form.low_stock_threshold} onChange={set('low_stock_threshold')} /></div>}
              <div><label style={lbl}>Reorder Level</label><input style={inp} type="number" min="0" value={form.reorder_level} onChange={set('reorder_level')} /></div>
              <div><label style={lbl}>Max Stock</label><input style={inp} type="number" min="0" value={form.max_stock} onChange={set('max_stock')} placeholder="Maximum capacity" /></div>
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Batch & Serial Tracking</label></div>
              {showField('batch_number') && <div><label style={lbl}>Batch Number{isRequired('batch_number') && <span style={req}>*</span>}</label><input style={inp} value={form.batch_number} onChange={set('batch_number')} placeholder="BATCH-001" required={isRequired('batch_number')} /></div>}
              <div><label style={lbl}>Serial Number</label><input style={inp} value={form.serial_number} onChange={set('serial_number')} placeholder="SN-001" /></div>
              <div><label style={lbl}>Manufacturing Date</label><StrictDateInput style={inp}  value={form.manufacturing_date} onChange={set('manufacturing_date')} /></div>
              {showField('expiry_date') && <div><label style={lbl}>Expiry Date{isRequired('expiry_date') && <span style={req}>*</span>}</label><StrictDateInput style={inp}  value={form.expiry_date} onChange={set('expiry_date')} required={isRequired('expiry_date')} /></div>}
              {showField('warehouse_location') && <div><label style={lbl}>Warehouse / Location</label><input style={inp} value={form.warehouse_location} onChange={set('warehouse_location')} placeholder="Rack A, Shelf 3" /></div>}
              <div><label style={lbl}>Location / Rack</label><input style={inp} value={form.location_rack} onChange={set('location_rack')} placeholder="Rack B-12" /></div>
              <div>{renderCheckBox("Print Expiry Date on Invoice", "print_expiry_date")}</div>
            </>}

            {/* ── PHYSICAL PROPERTIES TAB ── */}
            {activeTab === 'physical' && <>
              {showField('color') && <div><label style={lbl}>Color{isRequired('color') && <span style={req}>*</span>}</label><input style={inp} value={form.color} onChange={set('color')} placeholder="Red, Blue, Black" required={isRequired('color')} /></div>}
              {showField('size') && <div><label style={lbl}>Size{isRequired('size') && <span style={req}>*</span>}</label><input style={inp} value={form.size} onChange={set('size')} placeholder="S, M, L, XL or 32, 34" required={isRequired('size')} /></div>}
              {showField('weight') && <div><label style={lbl}>Weight (kg){isRequired('weight') && <span style={req}>*</span>}</label><input style={inp} type="number" step="0.001" min="0" value={form.weight} onChange={set('weight')} required={isRequired('weight')} /></div>}
              {showField('dimensions') && <div><label style={lbl}>Dimensions (L×W×H){isRequired('dimensions') && <span style={req}>*</span>}</label><input style={inp} value={form.dimensions} onChange={set('dimensions')} placeholder="30x20x10 cm" required={isRequired('dimensions')} /></div>}
              <div><label style={lbl}>Model Number</label><input style={inp} value={form.model_number} onChange={set('model_number')} placeholder="MDL-2024" /></div>
            </>}

            {/* ── SERVICE TAB ── */}
            {activeTab === 'service' && form.item_type === 'Service' && <>
              <div><label style={lbl}>Service Duration</label><input style={inp} value={form.service_duration} onChange={set('service_duration')} placeholder="30 min, 1 hour" /></div>
              <div><label style={lbl}>Service Location</label><input style={inp} value={form.service_location} onChange={set('service_location')} placeholder="At shop, Home visit" /></div>
              <div><label style={lbl}>Technician / Staff</label><input style={inp} value={form.technician} onChange={set('technician')} placeholder="Assigned staff" /></div>
              <div><label style={lbl}>Cost of Service (₹)</label><input style={inp} type="number" step="0.01" min="0" value={form.cost_of_service} onChange={set('cost_of_service')} /></div>
              <div><label style={lbl}>Commission %</label><input style={inp} type="number" step="0.01" min="0" max="100" value={form.commission_percent} onChange={set('commission_percent')} /></div>
              <div>{renderCheckBox("Appointment Required", "appointment_required")}</div>
            </>}

            {/* ── ADVANCED TAB ── */}
            {activeTab === 'advanced' && <>
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '8px 0' }}>
                {renderCheckBox("Print Description", "print_description")}
                {renderCheckBox("One Click Sale", "one_click_sale")}
                {renderCheckBox("Enable Tracking", "enable_tracking")}
                {renderCheckBox("Print Serial No.", "print_serial")}
                {renderCheckBox("Not For Sale", "not_for_sale")}
                {renderCheckBox("Print IMEI / Serial", "print_imei")}
                {renderCheckBox("Internal Product", "internal_product")}
                {renderCheckBox("Serial Tracking", "serial_tracking")}
              </div>
              <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${border}`, paddingTop: 10 }}><label style={{ ...lbl, fontSize: 12, color: acc }}>Warranty</label></div>
              <div><label style={lbl}>Warranty Period</label><input style={inp} type="number" min="0" value={form.warranty} onChange={set('warranty')} placeholder="e.g. 12" /></div>
              <div><label style={lbl}>Warranty Type</label>
                <select style={inp} value={form.warranty_type} onChange={set('warranty_type')}>
                  <option value="months">Months</option><option value="years">Years</option><option value="days">Days</option>
                </select>
              </div>
            </>}
          </div>
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleClose} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Saving…' : product ? 'Update' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

export default ProductModal;
